import type {
  ThemePagesQueryResponse,
  ThemePagesQueryVariables,
} from '~/api/graphql/queries/themePages.generated';
import type { ThemePageEdgeSelectFragment } from '~/api/graphql/fragments/themePage.generated';

import { ThemePagesDocument } from '~/api/graphql/queries/themePages.generated';
import { useThemePageStore } from '~/stores/themePage';
import { chunkArray, mapThemePagesV7 } from '~/utils';
import { useQueries } from '@tanstack/react-query';
import { IThemePageType } from '~/types/themePage';
import { appFetcher } from '~/api/utils';
import { useMemo } from 'react';

const CHUNK_SIZE = 100;

export const useQueryThemePagesPaginated = (
  themeId: string | undefined,
  templateIds?: string[],
) => {
  const upsertThemePages = useThemePageStore((state) => state.upsertThemePages);
  const setIdsFetched = useThemePageStore((state) => state.setIdsFetched);

  const templateChunks = useMemo(() => {
    if (!templateIds?.length) return [];

    return chunkArray(templateIds, CHUNK_SIZE);
  }, [templateIds]);

  const queries = useQueries({
    queries: templateChunks.map((chunk) => ({
      queryKey: ['ThemePages', themeId, chunk],
      queryFn: async () => {
        const response = await appFetcher<ThemePagesQueryResponse, ThemePagesQueryVariables>(
          ThemePagesDocument,
          {
            where: {
              themeID: themeId ?? '',
              idIn: chunk,
              typeNotIn: [IThemePageType.STATIC],
            },
          },
        )();
        return response || [];
      },
      enabled: !!themeId && !!chunk?.length,
      onSuccess: (data: ThemePagesQueryResponse) => {
        if (!chunk?.length) return;

        const themePages = data?.themePages?.edges as ThemePageEdgeSelectFragment[];
        const themePagesMapping = mapThemePagesV7(themePages);

        upsertThemePages(themePagesMapping);
        setIdsFetched(chunk);
      },
    })),
  });

  return queries;
};
